import React from "react";
import { Tooltip } from "@mui/material";
import {
  getCallActionsForContext,
  isDynamicForwardAvailable,
  isElevenLabsAvailable,
  DYNAMIC_FORWARD_DISABLED_NOTE,
} from "../../config/callActionOptions";
import { useElevenLabsDisclaimer } from "../../hooks/useElevenLabsDisclaimer";
import { useDynamicForwardUpgrade } from "../../hooks/useDynamicForwardUpgrade";
import { useRouteConfigModals } from "../contexts/RouteConfigModalsContext";
import ElevenLabsBetaDisclaimerModal from "./ElevenLabsBetaDisclaimerModal";
import DynamicForwardUpgradeModal from "./DynamicForwardUpgradeModal";

const ELEVENLABS_DISABLED_NOTE = "You must enable recording and transcription to use ElevenLabs.";
const IVR_DISABLED_NOTE = "You can't configure separate business hours here because Business Hours are already enabled at the route level.";

const CallActionOptionsList = ({
  context,
  route,
  enableBusinessHours = false,
  onSelect,
}) => {
  const actions = getCallActionsForContext(context);
  const planDetails = useRouteConfigModals()?.planDetails;
  const elevenLabsAvailable = isElevenLabsAvailable(route);
  const dynamicForwardAvailable = isDynamicForwardAvailable(planDetails);
  const {
    requestConfirmation,
    disclaimerOpen,
    acknowledgeDisclaimer,
    cancelDisclaimer,
  } = useElevenLabsDisclaimer();
  const {
    requestUpgrade,
    upgradeOpen,
    goToBilling,
    cancelUpgrade,
  } = useDynamicForwardUpgrade();

  const handleSelectAction = (actionType) => {
    if (actionType === "dynamic_forward" && !dynamicForwardAvailable) {
      requestUpgrade();
      return;
    }
    if (actionType === "ai_agent" && !elevenLabsAvailable) return;
    if (actionType === "ivr" && enableBusinessHours) return;
    if (actionType === "ai_agent") {
      requestConfirmation(() => onSelect(actionType));
      return;
    }
    onSelect(actionType);
  };

  return (
    <>
      <div className="flex flex-col space-y-3">
        {actions.map((action, idx) => {
          const isElevenLabs = action.type === "ai_agent";
          const isDynamicForward = action.type === "dynamic_forward";
          const isIvr = action.type === "ivr";
          const elevenLabsDisabled = isElevenLabs && !elevenLabsAvailable;
          const dynamicForwardLocked = isDynamicForward && !dynamicForwardAvailable;
          const ivrDisabled = isIvr && enableBusinessHours;
          const disabled = elevenLabsDisabled || ivrDisabled;
          const tooltipText = elevenLabsDisabled
            ? ELEVENLABS_DISABLED_NOTE
            : dynamicForwardLocked
              ? DYNAMIC_FORWARD_DISABLED_NOTE
              : ivrDisabled
                ? IVR_DISABLED_NOTE
                : "";
          const subtitle = disabled || dynamicForwardLocked ? tooltipText : action.subtitle;
          const rowClass = disabled
            ? "opacity-60 cursor-not-allowed"
            : dynamicForwardLocked
              ? "opacity-60 hover:bg-gray-100 cursor-pointer"
              : "hover:bg-gray-100 cursor-pointer";

          return (
            <Tooltip key={action.type} title={tooltipText} placement="top">
              <div
                onClick={() => handleSelectAction(action.type)}
                className={`flex items-start space-x-3 p-3 rounded-lg ${
                  idx === 0 ? "bg-gray-50" : ""
                } ${rowClass} transition`}
              >
                <div className="mt-1">{action.icon}</div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {action.title}
                    {dynamicForwardLocked && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        Team Plan
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">{subtitle}</div>
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>
      <ElevenLabsBetaDisclaimerModal
        open={disclaimerOpen}
        onAcknowledge={acknowledgeDisclaimer}
        onCancel={cancelDisclaimer}
      />
      <DynamicForwardUpgradeModal
        open={upgradeOpen}
        onUpgrade={goToBilling}
        onCancel={cancelUpgrade}
      />
    </>
  );
};

export default CallActionOptionsList;
